// src/pages/Variants/Variants.jsx
import React, { useState, useEffect } from 'react';
import { Table, Button, Card, Alert, Row, Col, Form, Badge } from 'react-bootstrap';
import { FaEdit, FaTrash, FaPlus, FaSearch, FaBox } from 'react-icons/fa';
import { variantsAPI } from '../../services/api';
import FormModal from '../../components/Common/FormModal';
import ConfirmModal from '../../components/Common/ConfirmModal';

const Variants = () => {
    const [variants, setVariants] = useState([]);
    const [filteredVariants, setFilteredVariants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedVariant, setSelectedVariant] = useState(null);

    const [formData, setFormData] = useState({
        sku: '',
        price: '',
        compare_price: '',
        is_active: '1',
        attributes: {
            color: '',
            size: ''
        }
    });

    useEffect(() => {
        fetchVariants();
    }, []);

    useEffect(() => {
        const filtered = variants.filter(variant =>
            variant.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            JSON.stringify(variant.attributes)?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredVariants(filtered);
    }, [searchTerm, variants]);

    const fetchVariants = async () => {
        try {
            const response = await variantsAPI.list({ page: 1, limit: 50 });
            setVariants(response.data);
            setFilteredVariants(response.data);
        } catch (err) {
            setError('Failed to fetch variants');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            // In a real app, you'd need the product ID
            const productId = 'demo_product_id';
            await variantsAPI.create(productId, formData);
            setShowCreateModal(false);
            resetForm();
            fetchVariants();
        } catch (err) {
            setError('Failed to create variant');
        }
    };

    const handleDelete = async () => {
        try {
            await variantsAPI.delete(selectedVariant.id);
            setShowDeleteModal(false);
            setSelectedVariant(null);
            fetchVariants();
        } catch (err) {
            setError('Failed to delete variant');
        }
    };

    const openDeleteModal = (variant) => {
        setSelectedVariant(variant);
        setShowDeleteModal(true);
    };

    const resetForm = () => {
        setFormData({
            sku: '',
            price: '',
            compare_price: '',
            is_active: '1',
            attributes: {
                color: '',
                size: ''
            }
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount || 0);
    };

    if (loading) return (
        <div className="text-center py-5">
            <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading...</span>
            </div>
        </div>
    );

    return (
        <div>
            <Row className="align-items-center mb-4">
                <Col>
                    <h1 className="mb-0">Product Variants</h1>
                    <p className="text-muted mb-0">Manage product variations and options</p>
                </Col>
                <Col xs="auto">
                    <Button
                        variant="success"
                        onClick={() => {
                            resetForm();
                            setShowCreateModal(true);
                        }}
                        className="d-flex align-items-center"
                    >
                        <FaPlus className="me-2" />
                        Create Variant
                    </Button>
                </Col>
            </Row>

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

            <Card className="border-0 shadow-sm mb-4">
                <Card.Body>
                    <Row className="g-3">
                        <Col md={6}>
                            <div className="position-relative">
                                <FaSearch className="position-absolute top-50 start-3 translate-middle-y text-muted" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search variants..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="ps-5"
                                />
                            </div>
                        </Col>
                        <Col md={6} className="text-md-end">
                            <small className="text-muted">
                                Showing {filteredVariants.length} of {variants.length} variants
                            </small>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <Table responsive hover className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th>SKU</th>
                                <th>Price</th>
                                <th>Attributes</th>
                                <th>Product</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredVariants.length > 0 ? (
                                filteredVariants.map(variant => (
                                    <tr key={variant.id}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                                    <FaBox className="text-white" />
                                                </div>
                                                <div>
                                                    <div className="fw-semibold">{variant.sku}</div>
                                                    <small className="text-muted">ID: {variant.id}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div>
                                                <div className="fw-semibold text-success">
                                                    {formatCurrency(variant.price)}
                                                </div>
                                                {variant.compare_price && (
                                                    <div className="small text-muted text-decoration-line-through">
                                                        {formatCurrency(variant.compare_price)}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex flex-wrap gap-1">
                                                {variant.attributes && Object.entries(variant.attributes).map(([key, value]) => (
                                                    <Badge key={key} bg="outline-secondary" text="dark">
                                                        {key}: {value}
                                                    </Badge>
                                                ))}
                                                {(!variant.attributes || Object.keys(variant.attributes).length === 0) && (
                                                    <span className="text-muted">No attributes</span>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="small">{variant.product_id}</div>
                                        </td>
                                        <td>
                                            <span className={`badge bg-${variant.is_active ? 'success' : 'secondary'}`}>
                                                {variant.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                >
                                                    <FaEdit className="me-1" />
                                                    Edit
                                                </Button>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    className="d-flex align-items-center"
                                                    onClick={() => openDeleteModal(variant)}
                                                >
                                                    <FaTrash className="me-1" />
                                                    Delete
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center py-4 text-muted">
                                        {searchTerm ? 'No variants found matching your search' : 'No variants found'}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            {/* Create Modal */}
            <FormModal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                title="Create New Variant"
                onSubmit={handleCreate}
                size="lg"
            >
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>SKU *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.sku}
                                onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                                required
                                placeholder="Enter SKU"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Status</Form.Label>
                            <Form.Select
                                value={formData.is_active}
                                onChange={(e) => setFormData({ ...formData, is_active: e.target.value })}
                            >
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Price *</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                value={formData.price}
                                onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                required
                                placeholder="0.00"
                                min="0"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Compare Price</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                value={formData.compare_price}
                                onChange={(e) => setFormData({ ...formData, compare_price: e.target.value })}
                                placeholder="0.00"
                                min="0"
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <h6 className="mb-3">Attributes</h6>
                <Row>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Color</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.attributes.color}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    attributes: { ...formData.attributes, color: e.target.value }
                                })}
                                placeholder="Enter color"
                            />
                        </Form.Group>
                    </Col>
                    <Col md={6}>
                        <Form.Group className="mb-3">
                            <Form.Label>Size</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.attributes.size}
                                onChange={(e) => setFormData({
                                    ...formData,
                                    attributes: { ...formData.attributes, size: e.target.value }
                                })}
                                placeholder="Enter size"
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={handleDelete}
                title="Delete Variant"
                message={`Are you sure you want to delete variant "${selectedVariant?.sku}"? This will also remove associated inventory.`}
            />
        </div>
    );
};

export default Variants;